import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import Header from '../Header';
import AdminNav from './AdminNav';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';

const PLATFORMS = ['android', 'ios'];

function PlatformForm({ platform, initial, authFetch }) {
    const [version, setVersion] = useState(initial?.version || '');
    const [url, setUrl] = useState(initial?.url || '');
    const [forceUpdate, setForceUpdate] = useState(initial?.forceUpdate || false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSaved(false);
        try {
            await authFetch(`/admin/app-version/${platform}`, {
                method: 'PUT',
                body: { version: version.trim(), url: url.trim(), forceUpdate },
            });
            setSaved(true);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'No se pudo guardar.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card elevation={2}>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ textTransform: 'capitalize' }}>{platform}</Typography>
                {initial?.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                        Última actualización: {new Date(initial.updatedAt).toLocaleString()}
                    </Typography>
                )}
                <Stack spacing={2} sx={{ mt: 2 }}>
                    <TextField label="Versión mínima" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.2.3" fullWidth />
                    <TextField label="URL de la tienda" value={url} onChange={(e) => setUrl(e.target.value)} fullWidth />
                    <FormControlLabel
                        control={<Switch checked={forceUpdate} onChange={(e) => setForceUpdate(e.target.checked)} />}
                        label="Forzar actualización (bloquea el juego hasta actualizar)"
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                    {saved && <Alert severity="success">Guardado.</Alert>}
                    <Button variant="contained" disabled={isSaving || !version.trim() || !url.trim()} onClick={handleSave}>
                        {isSaving ? <CircularProgress size={20} /> : 'Guardar'}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function AdminAppVersionScreen() {
    const { authFetch } = useAuth();
    const [byPlatform, setByPlatform] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const result = await authFetch('/admin/app-version');
                const map = {};
                for (const item of result.items) map[item.platform] = item;
                setByPlatform(map);
            } catch (err) {
                setError(err instanceof ApiError ? err.message : 'No se pudo cargar la configuración.');
            } finally {
                setIsLoading(false);
            }
        })();
    }, [authFetch]);

    return (
        <>
            <Header title="Configuración general" showSettings={false} />
            <AdminNav />
            <Container maxWidth="sm" sx={{ mt: 3, mb: 4 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        {PLATFORMS.map((platform) => (
                            <PlatformForm key={platform} platform={platform} initial={byPlatform?.[platform]} authFetch={authFetch} />
                        ))}
                    </Stack>
                )}
            </Container>
        </>
    );
}
